import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import { logError } from '../utils/errorLogger';
import { Box, Typography, Button, Alert, CircularProgress, Select, MenuItem, FormControl, InputLabel, TextField, Chip, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Layout from '../components/Layout';
import { usePageMeta } from '../utils/pageMeta';
import { useBranding } from '../contexts/BrandingContext';

const gold = '#C9A84C';

// The order form's text fields. Labels sit above the box all the time rather
// than inside it until there is a value: a phone's autofill can fill a field
// without MUI noticing, which left the label printed over the text.
const orderField = {
  InputLabelProps: { shrink: true },
  InputProps: { notched: true },
  sx: {
    '& .MuiInputBase-root': { color: 'white' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
  },
};

export default function TorchStore() {
  const branding = useBranding();
  usePageMeta('Torch Store | {club}', 'The official Torch Store. Order {club} gear online, pick up at {facility} in Orlando, FL.');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sizeSel, setSizeSel] = useState({});
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState('');
  // Bulk orders: a list people add themselves to rather than a checkout.
  // Nobody pays here — the club orders once enough want one and asks for the
  // money afterwards, which is how the first of these was run on a spreadsheet.
  const [bulk, setBulk] = useState([]);
  const [bulkForm, setBulkForm] = useState({});
  const [bulkBusy, setBulkBusy] = useState(null);
  const [bulkDone, setBulkDone] = useState({});
  // Whether the club has marked them paid — ticked by an admin when the money
  // arrives, since nothing is charged here.
  const [bulkPaid, setBulkPaid] = useState({});
  // How many of those are paid for: somebody can pay for one and then ask for two.
  const [bulkPaidQty, setBulkPaidQty] = useState({});
  const [bulkDelivered, setBulkDelivered] = useState({});

  // A visitor with no account has nothing to look their pledge up with: the
  // lookup needs a uid, and the list itself is admin-read, so asking the server
  // "what did this phone number order" would hand the private list out one
  // number at a time. Their own quantity is kept in their own browser instead,
  // which covers coming back to check. A different device or a cleared browser
  // forgets, and then the phone match on the server is what stops a duplicate.
  const [localPledge, setLocalPledge] = useState({});
  // What the signed-in account cannot fill in for them. Nearly always nothing:
  // of 208 accounts, 7 hold no phone and 6 no full name. Those few are asked,
  // rather than being put on the list with no way to be reached.
  const [memberGaps, setMemberGaps] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Logged-in visitors get the app Layout (menu); the public gets the standalone storefront.
  const [isAuthed, setIsAuthed] = useState(!!auth.currentUser);

  useEffect(() => onAuthStateChanged(auth, u => setIsAuthed(!!u)), []);

  // Read once, on the viewer's own doc: which details the order form still has
  // to ask for, and whether this is an admin — who gets a way through to the
  // order list, since the page itself never shows who is on it.
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) { setMemberGaps(null); setIsAdmin(false); return; }
    getDoc(doc(db, 'users', u.uid))
      .then(snap => {
        const d = snap.exists() ? snap.data() : {};
        setIsAdmin((d.roles || []).includes('admin'));
        setMemberGaps({
          name: !String(d.firstName || '').trim() || !String(d.lastName || '').trim(),
          phone: !String(d.phone || '').trim(),
          email: !String(d.email || '').trim() && !u.email,
        });
      })
      .catch(() => setMemberGaps({ name: false, phone: false, email: false }));
  }, [isAuthed]);

  useEffect(() => {
    // Open orders, plus one that has been placed or has arrived: its sign-ups
    // are closed, but the people on it watch it here until their balls are
    // handed out. A closed order drops off.
    getDocs(collection(db, 'bulkOrders'))
      .then(snap => setBulk(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        // Waiting on stock still takes names — that is when people join.
        .filter(o => ['collecting', 'on_hold', 'ordered', 'arrived'].includes(o.status))
        .sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99))))
      .catch(err => console.error('bulk orders load failed:', err));
  }, []);

  // What this member already asked for, so the form says "change" rather than
  // asking a second time. Only a signed-in visitor has a row to find.
  useEffect(() => {
    if (!isAuthed || bulk.length === 0) return;
    bulk.forEach(o => {
      httpsCallable(getFunctions(), 'myBulkOrderSignup')({ orderId: o.id })
        .then(({ data }) => {
          if (data?.signup) {
            setBulkDone(prev => ({ ...prev, [o.id]: data.signup.quantity }));
            setBulkPaid(prev => ({ ...prev, [o.id]: data.signup.paid === true }));
            setBulkPaidQty(prev => ({ ...prev, [o.id]: Number(data.signup.quantityPaid) || 0 }));
            setBulkDelivered(prev => ({ ...prev, [o.id]: data.signup.delivered === true }));
            setBulkForm(prev => ({ ...prev, [o.id]: { ...(prev[o.id] || {}), quantity: data.signup.quantity } }));
          }
        })
        .catch(() => {});
    });
  }, [isAuthed, bulk]);

  const PLEDGE_KEY = 'torchGroupOrderPledges';

  const readPledges = () => {
    try { return JSON.parse(window.localStorage.getItem(PLEDGE_KEY) || '{}'); }
    catch (e) { return {}; }
  };

  // Read once on arrival, for the visitor who signed up and came back.
  useEffect(() => { setLocalPledge(readPledges()); }, []);

  const rememberPledge = (orderId, quantity, firstName) => {
    try {
      const all = { ...readPledges(), [orderId]: { quantity, firstName, at: Date.now() } };
      window.localStorage.setItem(PLEDGE_KEY, JSON.stringify(all));
      setLocalPledge(all);
    } catch (e) {
      // Private browsing, or storage turned off. The pledge is already saved
      // on the server; only the reminder is lost.
      setLocalPledge(prev => ({ ...prev, [orderId]: { quantity, firstName } }));
    }
  };

  const joinBulk = async (order) => {
    const f = bulkForm[order.id] || {};
    const qty = Number(f.quantity);
    if (!Number.isFinite(qty) || qty < 0) { setError('Choose how many you want.'); return; }
    setBulkBusy(order.id); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'joinBulkOrder');
      const { data } = await fn({
        orderId: order.id,
        quantity: qty,
        // Ignored for a signed-in member: the server takes their details from
        // their account rather than from this form.
        firstName: f.firstName || '', lastName: f.lastName || '',
        phone: f.phone || '', email: f.email || '',
      });
      setBulkDone(prev => ({ ...prev, [order.id]: data.quantity }));
      // Raising the quantity after paying leaves the extra owing, so Paid is
      // re-read against the new number rather than kept from before.
      setBulkPaid(prev => ({ ...prev, [order.id]: data.quantity > 0 && (bulkPaidQty[order.id] || 0) >= data.quantity }));
      if (!isAuthed) rememberPledge(order.id, data.quantity, data.firstName);
      setBulk(prev => prev.map(o => o.id === order.id
        ? { ...o, committedQuantity: data.committedQuantity, committedPeople: data.committedPeople } : o));
    } catch (e) {
      logError('TorchStore', `Bulk order join failed for ${order.id}`, `${e?.code || '-'}: ${e?.message || e}`);
      setError(e?.message || 'Could not add you to the list. Please try again.');
    }
    setBulkBusy(null);
  };

  useEffect(() => {
    getDocs(collection(db, 'apparel'))
      .then(snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(i => i.active !== false)
          .sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99) || (a.name || '').localeCompare(b.name || ''));
        setItems(list);
      })
      .catch(err => { console.error('apparel load failed:', err); setError('Could not load apparel.'); })
      .finally(() => setLoading(false));
  }, []);

  const handleBuy = async (item) => {
    const sizes = Array.isArray(item.sizes) ? item.sizes : [];
    const size = sizeSel[item.id] || '';
    if (sizes.length > 0 && !size) { setError(`Please pick a size for ${item.name}.`); return; }
    setError('');
    setBuying(item.id);
    try {
      const functions = getFunctions();
      const create = httpsCallable(functions, 'createApparelCheckoutSession');
      const r = await create({
        itemId: item.id,
        size,
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/torch-store`,
      });
      if (r.data?.url) window.location.assign(r.data.url);
      else throw new Error('No checkout URL returned');
    } catch (e) {
      // A checkout that never starts leaves nothing behind — no Stripe session,
      // no order, just a shopper who gave up — and console.error only ever
      // reached their own devtools. Recorded so the next one is visible here.
      logError('TorchStore', `Checkout failed for ${item.id}`, `${e?.code || 'no-code'}: ${e?.message || e}`);
      // The server's own words when it has useful ones ("This item has no price
      // configured", "Please pick a valid size"). An internal failure keeps the
      // generic line: its message carries Stripe's wording, which is not for
      // buyers to read.
      const fromServer = e?.code && e.code !== 'internal' && e?.message;
      setError(fromServer ? e.message : 'Could not start checkout. Please try again.');
      setBuying(null);
    }
  };

  const content = (
    <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
      <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: 3, color: gold, textAlign: 'center' }}>
        Torch Store
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', mb: 3, fontSize: '0.9rem' }}>
        Order online &middot; pick up at {branding.facilityName}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: gold }} /></Box>}
      {!loading && items.length === 0 && (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 6 }}>
          No items available right now &mdash; check back soon!
        </Typography>
      )}

      {/* Bulk orders first: they close when the order is placed, so they are
          the time-limited thing on the page. */}
      {bulk.map(order => {
        const f = bulkForm[order.id] || {};
        // What this viewer already asked for: from their account if signed in,
        // otherwise from what this browser remembers.
        const remembered = localPledge[order.id];
        const mine = bulkDone[order.id] ?? (isAuthed ? undefined : remembered?.quantity);
        const set = (k) => (e) => setBulkForm(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), [k]: e.target.value } }));
        return (
          <Box key={order.id} sx={{
            mb: 3, p: 2.5, borderRadius: 2,
            backgroundColor: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.45)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem' }}>{order.name}</Typography>
              <Chip size="small" label="Group order" sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700 }} />
              <Typography sx={{ color: gold, fontWeight: 700 }}>
                ${((Number(order.unitPriceCents) || 0) / 100).toFixed(2)} each
              </Typography>
            </Box>
            {order.description && (
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', mb: 1 }}>{order.description}</Typography>
            )}
            {/* No target to show: there is no minimum or maximum for how many
                the club buys, so the number is just where the list stands. */}
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mb: 1.5 }}>
              {Number(order.committedQuantity) || 0} committed so far
              {/* The ways to pay sit right under the total whatever the status:
                  some people would rather settle up when they put their name down. */}
              {' · Pay by Venmo @coronabeachfl or pay by Zelle 321-689-1123'}
            </Typography>
            {/* Who is in, for everyone. Read from the order document, where the
                server keeps a three-field projection — the signup rows carry a
                phone number, an email and whether they have paid, and this page
                cannot read them. Nobody can change anything from here; a
                quantity moves only through the form above or an admin. */}
            {Array.isArray(order.roster) && order.roster.length > 0 && (
              <Box sx={{ mb: 1.5, border: '1px solid rgba(201,168,76,0.25)', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{
                  display: 'flex', px: 1.5, py: 0.75,
                  backgroundColor: 'rgba(201,168,76,0.12)',
                }}>
                  <Typography sx={{ color: gold, fontSize: '0.72rem', letterSpacing: 1, flexGrow: 1 }}>
                    WHO IS IN
                  </Typography>
                  <Typography sx={{ color: gold, fontSize: '0.72rem', letterSpacing: 1 }}>
                    BALLS
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                  {order.roster.map((r, i) => (
                    <Box key={`${r.firstName}-${r.lastInitial}-${i}`} sx={{
                      display: 'flex', px: 1.5, py: 0.6,
                      borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    }}>
                      <Typography sx={{
                        color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', flexGrow: 1,
                        display: 'flex', alignItems: 'center', gap: 0.5,
                      }}>
                        {`${r.firstName} ${r.lastInitial || ''}`.trim() || 'Added by phone'}
                        {/* The furthest step reached, not two separate marks:
                            one tick paid, two picked up. Borrowed from a chat
                            app on purpose — nobody needs the legend explained. */}
                        {r.delivered ? (
                          <Tooltip title="Paid and picked up">
                            <DoneAllIcon sx={{ fontSize: 16, color: '#5FD38D' }} />
                          </Tooltip>
                        ) : r.paid ? (
                          <Tooltip title="Paid">
                            <CheckIcon sx={{ fontSize: 16, color: '#5FD38D' }} />
                          </Tooltip>
                        ) : null}
                      </Typography>
                      <Typography sx={{ color: 'white', fontSize: '0.85rem', fontWeight: 700, minWidth: 24, textAlign: 'right' }}>
                        {r.quantity}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {/* Said once, at the bottom, so the ticks need no explaining. */}
                <Box sx={{
                  display: 'flex', gap: 1.5, px: 1.5, py: 0.75, flexWrap: 'wrap',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#5FD38D' }} /> paid
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <DoneAllIcon sx={{ fontSize: 14, color: '#5FD38D' }} /> picked up
                  </Typography>
                  {Number(order.deliveredQuantity) > 0 && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem' }}>
                      · {order.deliveredQuantity} of {order.committedQuantity} handed out
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* An admin needs the rest of it — phones, emails, who has paid. */}
            {isAdmin && (
              <Button size="small" variant="outlined" onClick={() => navigate('/admin/store-items')}
                sx={{ mb: 1.5, color: gold, borderColor: 'rgba(201,168,76,0.5)' }}>
                View current order ({Number(order.committedQuantity) || 0} committed
                {Number(order.paidQuantity) ? `, ${order.paidQuantity} paid` : ''})
              </Button>
            )}
            {order.status === 'arrived' && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                {order.statusNote || 'They are in — ready for pickup at Corona Beach.'}
              </Alert>
            )}
            {order.status === 'ordered' && (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                {order.statusNote
                  || 'Ordered — sign-ups are closed. We will let you know when they are in and ready to collect.'}
              </Alert>
            )}
            {order.status === 'on_hold' && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {order.statusNote
                  || 'On hold while we wait on stock. Put your name down now — we will let the group chat know when they are in.'}
              </Alert>
            )}
            {mine != null && (
              <Alert severity={bulkPaid[order.id] ? 'success' : 'info'} sx={{ mb: 1.5 }}>
                {remembered?.firstName && !isAuthed ? `${remembered.firstName}, you are` : 'You are'} down
                for {mine}.
                {bulkDelivered[order.id]
                  ? ' Paid and picked up — enjoy.'
                  : bulkPaid[order.id]
                    ? (order.status === 'arrived'
                      ? ' Paid — they are in, so come pick yours up.'
                      : ' Paid — thank you. We will let you know when it is ready to collect.')
                    : bulkPaidQty[order.id] > 0
                      ? ` Paid for ${bulkPaidQty[order.id]} — ${mine - bulkPaidQty[order.id]} still to pay.`
                      : order.status === 'arrived' ? ' They are in — come pick yours up.'
                      : order.status === 'ordered' ? ' The order is placed — see you at pickup.'
                        : ' Change it below any time before the order is placed.'}
              </Alert>
            )}
            {order.status !== 'ordered' && order.status !== 'arrived' && <>
            <Box sx={{ display: 'flex', columnGap: 1, rowGap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{
                minWidth: 130,
                '& .MuiInputBase-root': { color: 'white' },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
                '& .MuiSvgIcon-root': { color: gold },
              }}>
                <InputLabel shrink>How many</InputLabel>
                <Select label="How many" notched displayEmpty value={f.quantity ?? ''} onChange={set('quantity')}
                  renderValue={v => (v === '' ? <span style={{ color: 'rgba(255,255,255,0.35)' }}>Choose</span> : v === 0 ? 'None' : v)}>
                  {[0, 1, 2, 3, 4, 5, 6].map(n => (
                    <MenuItem key={n} value={n}>{n === 0 ? 'None — take me off' : n}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Asked only of somebody the club does not already know — and of
                  the few members whose own account is missing a detail. */}
              {(!isAuthed || memberGaps?.name) && (
                <>
                  <TextField size="small" label="First name" value={f.firstName || ''} onChange={set('firstName')}
                    {...orderField} />
                  <TextField size="small" label="Last name" value={f.lastName || ''} onChange={set('lastName')}
                    {...orderField} />
                </>
              )}
              {(!isAuthed || memberGaps?.phone) && (
                <TextField size="small" label="Phone" value={f.phone || ''} onChange={set('phone')}
                  helperText={isAuthed ? 'We do not have one on file' : ''}
                  FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
                  {...orderField} />
              )}
              {(!isAuthed || memberGaps?.email) && (
                <TextField size="small" label="Email" value={f.email || ''} onChange={set('email')}
                  {...orderField} />
              )}
              <Button variant="contained" disabled={bulkBusy === order.id || f.quantity == null || f.quantity === ''}
                onClick={() => joinBulk(order)}
                sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}>
                {bulkBusy === order.id ? <CircularProgress size={20} sx={{ color: '#0D1E35' }} />
                  : mine != null ? 'Update' : 'Add me to the list'}
              </Button>
            </Box>
            {/* For the visitor whose browser does not remember them — a second
                device, or storage cleared. A repeat entry sets the quantity
                rather than adding to it, and saying so is the difference
                between asking for one more and ending up with one. */}
            {mine == null && !isAuthed && (
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', mt: 1 }}>
                Added yourself already? Entering the same phone number again changes how many
                you are down for &mdash; it does not add to it.
              </Typography>
            )}
            </>}
          </Box>
        );
      })}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
        {items.map(item => {
          const sizes = Array.isArray(item.sizes) ? item.sizes : [];
          return (
            <Box key={item.id} sx={{
              backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.3)',
              borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column',
            }}>
              {item.imageUrl && (
                <Box component="img" src={item.imageUrl} alt={item.name}
                  sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1 }}>
                <Typography sx={{ color: 'white', fontWeight: 700 }}>{item.name}</Typography>
                {item.description && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>{item.description}</Typography>
                )}
                <Typography sx={{ color: gold, fontWeight: 700, fontSize: '1.15rem' }}>
                  ${Number(item.price || 0).toFixed(2)}
                </Typography>
                {sizes.length > 0 && (
                  <FormControl size="small" fullWidth sx={{
                    '& .MuiInputBase-root': { color: 'white' },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
                    '& .MuiSvgIcon-root': { color: gold },
                  }}>
                    <InputLabel>Size</InputLabel>
                    <Select label="Size" value={sizeSel[item.id] || ''}
                      onChange={e => setSizeSel(prev => ({ ...prev, [item.id]: e.target.value }))}>
                      {sizes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Button variant="contained" disabled={buying === item.id} onClick={() => handleBuy(item)}
                  sx={{ mt: 'auto', backgroundColor: gold, color: '#0D1E35', fontWeight: 700, '&:hover': { backgroundColor: '#e0be6a' } }}>
                  {buying === item.id ? <CircularProgress size={20} sx={{ color: '#0D1E35' }} /> : 'Buy'}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>

      {!isAuthed && (
        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          <a href="/" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>&larr; Back to home</a>
        </Typography>
      )}
    </Box>
  );

  if (isAuthed) {
    return (
      <Layout>
        <Box sx={{ p: { xs: 2, sm: 3 }, background: '#0D1E35', minHeight: '100%', boxSizing: 'border-box' }}>
          {content}
        </Box>
      </Layout>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0D1E35', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 4 }}>
      <Box component="img" src="/horzlogo.png" alt="Torch Beach Volleyball"
        sx={{ maxWidth: 300, width: '75vw', cursor: 'pointer', mb: 3 }} onClick={() => navigate('/')} />
      {content}
    </Box>
  );
}
